/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../Modal/Modal';
import { GhostButton } from '../../styles/ui/Button';
import { TextLink } from '../../styles/ui/Link';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';

// ---------------------------------------------------------------------------
// AVISO de saldo negativo (handoff 0093 §5; rework 2026-08-16).
//
// Não é modal de erro: estoque negativo é aceito temporariamente e o registro
// nunca é bloqueado. Desde o modal unificado de confirmação
// (`MovementConfirmModal`), este componente é um VIEWER de detalhe — aberto
// pelo "ver" do indicador; a decisão de registrar acontece no modal unificado
// do Salvar, não aqui.
//
// Exporta também:
// - `NegativeStockTable` — a tabela de saldos, reusada pelo modal unificado
//   (componentizada, não copiada);
// - `StockImpactHint` — indicador discreto pra seção de Itens, com o
//   "↻ Atualizar" (re-consulta operacional sob demanda).
// ---------------------------------------------------------------------------

const WarnBanner = styled.p`
   margin: 0 0 14px;
   padding: 10px 12px;
   border-radius: 10px;
   border: 1px solid rgba(138, 90, 0, 0.35);
   background: rgba(255, 179, 0, 0.12);
   color: #6b4a00;
   font-size: 13.5px;
   font-weight: 600;
   line-height: 1.45;
`;

const NegativeQty = styled.strong`
   color: #ab2020;
`;

function formatQty(value) {
   if (value === null || value === undefined) return '—';
   return String(value);
}

// Tabela de saldos que ficarão negativos — usada aqui e no
// MovementConfirmModal.
export function NegativeStockTable({ negativeStock = [] }) {
   return (
      <TableWrapper>
         <Table>
            <thead>
               <tr>
                  <th className="center">SKU</th>
                  <th>Produto</th>
                  <th className="center">Saldo atual</th>
                  <th className="center">Saldo resultante</th>
               </tr>
            </thead>
            <tbody>
               {negativeStock.map((item) => (
                  <tr key={`${item.pdv_id}-${item.prod_id}`}>
                     <td className="center">{item.prod_sku ?? '—'}</td>
                     <td>{item.prod_name ?? item.prod_id}</td>
                     <td className="center">{formatQty(item.current_qtd)}</td>
                     <td className="center">
                        <NegativeQty>
                           {formatQty(item.resulting_qtd)}
                        </NegativeQty>
                     </td>
                  </tr>
               ))}
            </tbody>
         </Table>
      </TableWrapper>
   );
}

const negativeStockShape = PropTypes.shape({
   pdv_id: PropTypes.number,
   prod_id: PropTypes.number,
   prod_sku: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   prod_name: PropTypes.string,
   current_qtd: PropTypes.number,
   resulting_qtd: PropTypes.number,
});

NegativeStockTable.propTypes = {
   negativeStock: PropTypes.arrayOf(negativeStockShape),
};

export default function StockImpactModal({
   isOpen,
   negativeStock = [],
   queryInfo = {},
   onClose,
}) {
   const { pdvName, moveTypeLabel } = queryInfo;

   const subtitle = [pdvName, moveTypeLabel].filter(Boolean).join(' · ');

   return (
      <Modal
         isOpen={isOpen}
         title="Produtos ficarão com saldo negativo"
         subtitle={subtitle || null}
         onClose={onClose}
         footer={
            <GhostButton type="button" onClick={onClose}>
               Fechar
            </GhostButton>
         }
         maxWidth="720px"
      >
         <WarnBanner>
            {negativeStock.length}{' '}
            {negativeStock.length > 1 ? 'produtos ficarão' : 'produto ficará'}{' '}
            com saldo negativo neste PDV após o registro. O saldo negativo é
            aceito temporariamente — o registro <strong>não</strong> é
            bloqueado, este é só um aviso.
         </WarnBanner>

         <NegativeStockTable negativeStock={negativeStock} />
      </Modal>
   );
}

StockImpactModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   negativeStock: PropTypes.arrayOf(negativeStockShape),
   queryInfo: PropTypes.shape({
      pdvName: PropTypes.string,
      moveTypeLabel: PropTypes.string,
   }),
   onClose: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Indicador discreto do estado do check, pra seção de Itens. Nada aparece em
// `idle`/`skipped` (falha degrada em silêncio — o recurso é informativo).
//
// `onRefresh` re-consulta o saldo sob demanda (fluxo operacional: "estou com
// saldo negativo no pedido, pode conferir?" → outro usuário ajusta o estoque
// → este clica em Atualizar → o aviso some se o impacto sumiu). O indicador
// NÃO se atualiza sozinho quando outra tela muda o estoque — só a guarda do
// Salvar é sempre fresca.
// ---------------------------------------------------------------------------

const HintText = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-top: 4px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $tone }) => {
      if ($tone === 'clear') return '#1b7f4d';
      if ($tone === 'found') return '#8a5a00';
      return 'rgba(0, 0, 0, 0.5)';
   }};
`;

function RefreshAction({ onRefresh }) {
   if (!onRefresh) return null;
   return (
      <TextLink
         as="button"
         type="button"
         onClick={onRefresh}
         style={{ fontSize: 'inherit' }}
         title="Consultar o saldo novamente"
      >
         ↻ Atualizar
      </TextLink>
   );
}

RefreshAction.propTypes = { onRefresh: PropTypes.func };

export function StockImpactHint({ status, count = 0, onOpen, onRefresh }) {
   if (status === 'checking') {
      return <HintText>Verificando impacto no saldo…</HintText>;
   }
   if (status === 'clear') {
      return (
         <HintText $tone="clear">
            ✓ Todos os itens aptos para movimento
            <RefreshAction onRefresh={onRefresh} />
         </HintText>
      );
   }
   if (status === 'found') {
      return (
         <HintText $tone="found">
            ⚠ {count} produto{count > 1 ? 's' : ''} ficar
            {count > 1 ? 'ão' : 'á'} com saldo negativo
            {onOpen ? (
               <TextLink
                  as="button"
                  type="button"
                  onClick={onOpen}
                  style={{ fontSize: 'inherit' }}
               >
                  ver
               </TextLink>
            ) : null}
            <RefreshAction onRefresh={onRefresh} />
         </HintText>
      );
   }
   return null; // idle | skipped
}

StockImpactHint.propTypes = {
   status: PropTypes.oneOf(['idle', 'checking', 'clear', 'found', 'skipped'])
      .isRequired,
   count: PropTypes.number,
   onOpen: PropTypes.func,
   onRefresh: PropTypes.func,
};
